/**
 * QuestionFlow.tsx — the server-driven question loop (one screen per answer).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: the flow renders ONLY what GET …/questions returns and honours every
 * `evaluationResult` verdict:
 *   pass      → advance (server's `nextQuestionKey` wins; positional otherwise)
 *   soft_stop → keep going, with a subtle "a clinician will review" note
 *   hard_stop → the stop screen (free callback inline; R31 signposting if mh)
 * R28 is why the flow REFETCHES the question set when it runs out of local
 * questions or is pointed at one it has not seen: restricted questions are only
 * included by the server once the eligibility gate has passed. Every API call
 * has a rendered, retryable error state (TA-06); `busy` guards double-submit.
 *
 * DOB CAPTURED ONCE TOO: the `date`-kind question (date of birth) is likewise
 * lifted to the parent via the optional `onDobChange` callback, fired whenever
 * a `date`-kind answer is submitted — JourneyPage stores it and threads it
 * into checkout's account step so the patient is never asked for it twice.
 *
 * HEIGHT/WEIGHT ASKED ONCE: Step 0 (CaptureStep) already captured height/weight
 * before this component ever mounts. The server's question set still leads with
 * one or more `capture`-kind questions (a confirm-your-details style re-ask,
 * possibly gated on a BMI floor) — those are auto-submitted programmatically
 * with the `capturePrefill` values on load, in idx order, honouring every
 * verdict (hard_stop still stops the patient here — e.g. a BMI-floor gate), and
 * are never rendered as a screen. `capture`-kind questions are excluded from the
 * visible/progress list entirely, so "Question X of N" only counts the questions
 * the patient actually answers. If `capturePrefill` is missing (0/0 — a patient
 * somehow reached this loop without Step-0 capture), auto-submit is skipped and
 * the leading capture renders normally instead, so the flow never crashes or
 * submits garbage. The auto-submit sequence is guarded by a ref so it cannot
 * double-fire under React 18 StrictMode's dev-only double effect invocation.
 *
 * RESUME (navigation resilience): the optional `initialQuestionKey` is
 * JourneyPage's resume pointer (progress restore OR hard-stop "Back") — after
 * the normal load/leading-capture-auto-submit sequence above runs exactly as
 * it always has, `load()` jumps straight to `initialQuestionKey` INSTEAD OF
 * the locally-computed "first unanswered" key, as long as the server's
 * refetched question set actually contains it (never trusts a stale/foreign
 * key). Hard-stop "Back" ALSO passes `seededAnswers` (the interactive answers
 * JourneyPage just replayed) so `answerLogRef` + the local answers map stay
 * coherent across remounts — without that, a second consecutive hard stop
 * would hand an empty `priorAnswers` to the next mint, and `visibleIf` /
 * goBack / "Question X of N" would read a stale empty map. When
 * `leadingCaptureSubmitted` is set, JourneyPage already posted the capture
 * during replay (capture-first, so BMI rules see H/W) and this flow must NOT
 * auto-submit it again.
 *
 * DIRTY (navigation resilience): the CURRENT `QuestionCard`'s "unsaved input
 * ahead of Continue" bubbles up via `onDirtyChange` into `cardDirty`, which
 * feeds `useUnsavedChangesGuard` — covering both a real router navigation
 * (browser Back/tab close, via `useBlocker`/`beforeunload`) and this flow's
 * OWN in-app Back/exit (`goBack`/`onExit`, which only change local state and
 * are not router navigations at all) through the SAME `guardedAction`/leave
 * dialog. `cardDirty` resets whenever the rendered question changes (a fresh
 * card never inherits the previous one's dirt). Since 2026-09-25 that dialog is
 * the patient journey's RETENTION variant (LeaveConfirmDialog's `journey` prop:
 * how much is left, measured exactly as the progress line is, "Stay and finish",
 * a quiet "Leave this step" link and this screen's call-back through the
 * provider below); `staffView` keeps the classic dialog.
 *
 * ⚠️ SINCE 2026-09-26 THE PATIENT'S BACK NEVER ASKS (Ashley Haveloch-Jones via
 * the CTO). On the patient journey the in-app Back and a browser/phone Back go
 * straight to the previous question, dropping only what was typed on this
 * screen and not sent; the retention dialog opens on EXIT INTENT instead (the
 * pointer heading for the tab strip — `navigation/useExitIntent.ts`), without
 * its leave link, since nothing is being left. A tab close or refresh still
 * gets the browser's own prompt from the guard's `beforeunload`. The staff
 * fill-on-behalf view (`staffView`) keeps the ask-first Back and the classic
 * dialog. See "BACK IS BACK" in the body.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getQuestions, submitAnswer } from '../api/journey';
import type { JourneyMarketingTicks } from '../marketing/journeyConsent';
import type { AnswerValue, Attribution, EvaluationResult, QuestionPublic } from '../api/types';
import { trackQuestionnaireStart, trackQuestionnaireStep } from '../tracking/dataLayer';
import { Alert, ErrorState } from '../ui/kit';
import { FadeIn } from '../ui/motion';
import { SkeletonCard } from '../ui/Skeleton';
import { LeaveConfirmDialog } from '../navigation/LeaveConfirmDialog';
import { useExitIntent } from '../navigation/useExitIntent';
import { useUnsavedChangesGuard } from '../navigation/useUnsavedChangesGuard';
import { JourneyCallbackProvider } from './JourneyCallbackContext';
import { JourneyScreen } from './JourneyScreen';
import { questionProgress, remainingQuestions, type ProgressInput, type RemainingQuestions } from './progressCopy';
import { QuestionCard } from './QuestionCard';
import { StopScreen } from './StopScreen';

/**
 * StrictMode can mount→unmount→remount on the SAME assessmentId; without this
 * the start event would fire twice in development. Keyed by assessmentId (not
 * leadId) so a genuine second attempt for the same lead (Projection → Continue
 * → fresh assessment) still counts as a new start.
 */
const questionnaireStartEmittedForAssessment = new Set<string>();

/**
 * TEST-ONLY: clears the StrictMode de-dupe so specs are isolated.
 * WHY: the Set is process-global (correct for StrictMode in the app) and
 * persists across tests in the same file. Our fixture assessment id is
 * `assess-1` almost everywhere — without a reset, the second spec that mounts
 * a flow with that id silently gets no `questionnaire_start`.
 */
export function __resetQuestionnaireStartTracking(): void {
  questionnaireStartEmittedForAssessment.clear();
}

type AnswerMap = Record<string, AnswerValue>;

/** One interactively-submitted answer, in submission order — used ONLY to
 * replay prior answers onto a fresh assessment when the patient hits "Back"
 * from a hard stop (see `onRestart` below). Leading `capture`-kind answers are
 * deliberately NOT logged here: a fresh mount re-derives them itself from
 * `capturePrefill`, so replaying them too would just double-submit. */
export interface AnsweredEntry {
  questionKey: string;
  value: AnswerValue;
  detail?: string;
}

/** The in-flow hard-stop context handed to `onRestart` — everything needed to
 * rebuild the patient's progress on a brand-new assessment (CLIN-03 forbids
 * reusing the stopped one) and land them back on the SAME question, ready to
 * pick a different answer. `null` when the stop fired on a leading `capture`
 * question (a BMI-floor gate, say) — there is no "different option" to offer
 * there (it's driven by height/weight, not a selectable answer), so the
 * caller falls back to its safe default restart instead. */
export interface HardStopRestartContext {
  stopQuestionKey: string;
  priorAnswers: AnsweredEntry[];
}

function isVisible(question: QuestionPublic, answers: AnswerMap): boolean {
  if (!question.visibleIf) return true;
  return answers[question.visibleIf.questionKey] === question.visibleIf.equals;
}

/** Sorted + visibleIf-filtered, WITHOUT excluding `capture` kind. Used only to
 * pick a fallback first question when there is no usable capturePrefill. */
function allVisibleQuestions(questions: QuestionPublic[], answers: AnswerMap): QuestionPublic[] {
  return [...questions].sort((a, b) => a.idx - b.idx).filter((q) => isVisible(q, answers));
}

/** The rendered/counted question list: `capture`-kind questions are asked ONCE
 * via the leading auto-submit in `load()` and never shown as a screen, so they
 * are excluded here — from the visible list, the progress count, and the
 * "Question X of N" label. */
function visibleQuestions(questions: QuestionPublic[], answers: AnswerMap): QuestionPublic[] {
  return allVisibleQuestions(questions, answers).filter((q) => q.kind !== 'capture');
}

function nextVisibleAfter(questions: QuestionPublic[], answers: AnswerMap, afterIdx: number): QuestionPublic | undefined {
  return visibleQuestions(questions, answers).find((q) => q.idx > afterIdx);
}

function answersFromSeed(seed: AnsweredEntry[]): AnswerMap {
  const map: AnswerMap = {};
  for (const entry of seed) map[entry.questionKey] = entry.value;
  return map;
}

export function QuestionFlow({
  assessmentId,
  clinicSlug,
  attribution,
  capturePrefill,
  leadId,
  staffView = false,
  initialQuestionKey,
  seededAnswers,
  leadingCaptureSubmitted = false,
  onCaptureChange,
  onDobChange,
  onComplete,
  onRestart,
  onExit,
  callbackOffer,
  marketing,
}: {
  assessmentId: string;
  clinicSlug: string;
  attribution?: Attribution;
  capturePrefill: { heightCm: number; weightKg: number };
  /** The step-0 lead — threaded into inline StopScreen callbacks for CRM deal linkage. */
  leadId?: string;
  /** Threaded to the hard-stop StopScreen: true when STAFF fill the form on a
   * patient's behalf (AdminFormPage) — the stop renders exactly as the patient
   * would see it, minus the nested CallbackForm. Defaults off so the
   * patient-facing journey is untouched. */
  staffView?: boolean;
  /**
   * The navigation-resilience resume pointer (JourneyPage's `GET …/progress`
   * read, `nextQuestionKey`) — when set and present in the (re)fetched
   * question set, `load()` jumps straight there instead of the locally-
   * computed first-unanswered key. `null`/absent/unknown-key = normal
   * behaviour, unchanged. See this file's RESUME doc block above.
   */
  initialQuestionKey?: string | null;
  /**
   * Interactive answers JourneyPage just replayed onto this assessment (hard-
   * stop "Back"). Seeds `answerLogRef` + the local answers map so a second
   * consecutive stop still has prior answers, and so `visibleIf` / goBack /
   * progress counts stay coherent. Never loaded from localStorage (R19).
   */
  seededAnswers?: AnsweredEntry[];
  /**
   * When true, JourneyPage already posted leading `capture` answers during
   * hard-stop resume (capture-first). Skip the auto-submit loop — re-posting
   * would race the server and undo the capture-first ordering.
   */
  leadingCaptureSubmitted?: boolean;
  onCaptureChange: (heightCm: number, weightKg: number) => void;
  /** Fired with the ISO `yyyy-mm-dd` value whenever a `date`-kind (DOB)
   * question is answered — optional so existing callers/specs are unaffected. */
  onDobChange?: (dateOfBirth: string) => void;
  /** Fired with the full interactive answer log once the last question is
   * answered — lets the post-submit not_eligible StopScreen replay back to
   * the last answered question instead of only offering a Projection restart. */
  onComplete: (answerLog: AnsweredEntry[]) => void;
  /**
   * The stop screen's back button, for an IN-FLOW hard stop (before the final
   * submit). REQUIRED, and MUST start a fresh assessment rather than
   * resuming/re-answering this one — CLIN-03 durably marks a stopped
   * assessment (`stoppedAt`) STICKY the moment the hard stop fires, so the
   * clinic-service REJECTS (409) any further answer/photo-upload/submit
   * against it, even if it is still nominally `state: 'draft'` and even for
   * the exact question that triggered the stop. There is deliberately no
   * internal fallback here (e.g. "just clear `stop` and re-show the current
   * question") — that would silently re-post to the now-409ing assessment.
   *
   * When the stop was triggered by an interactively-answered question (the
   * common case), a `HardStopRestartContext` is passed: the caller is
   * expected to mint a brand-new assessment, replay `priorAnswers` onto it in
   * order (each via `submitAnswer`), then re-render this component pointed
   * at the new assessment with `initialQuestionKey: stopQuestionKey` — that
   * lands the patient right back on the question that stopped them, free to
   * pick a different answer, without redoing the whole questionnaire or
   * detouring through Projection.
   *
   * Called with NO argument when the stop instead fired on a leading
   * `capture` question (a BMI-floor gate) — there is no single "different
   * option" to offer for a height/weight-driven stop, so the caller should
   * fall back to its default (safe, Projection-based) restart.
   */
  onRestart: (context?: HardStopRestartContext) => void;
  /** Back target for the FIRST question (there is no previous question to
   * re-show). The patient journey passes the stage-back callback (returns to
   * route choice, same `setStage` mechanism as every other journey screen);
   * omitted (e.g. the staff form flow) the first question shows no Back. */
  onExit?: () => void;
  /**
   * The in-journey call-back offer, built by JourneyPage. Rendered under the
   * QUESTION screens only — never under this flow's own stop screen, which
   * carries the call-back form inline already, and never while loading. The
   * staff fill-on-behalf flow passes none.
   *
   * With an offer, the question screen is wrapped in a
   * `JourneyCallbackProvider`, so the "Why we ask" dialog can open THIS offer's
   * call-back dialog (2026-09-24, see JourneyCallbackContext.tsx). With none,
   * there is no provider and "Why we ask" offers no call-back.
   */
  callbackOffer?: ReactNode;
  /**
   * The optional "Keeping in touch" marketing ticks for the consent screen
   * (QuestionCard renders them on the multi-option consent question only).
   * Threaded, never owned: JourneyPage holds the state so the tick survives
   * this flow remounting, and records it only after a successful submit.
   * Absent (the staff fill-on-behalf flow) — no block renders anywhere.
   */
  marketing?: JourneyMarketingTicks;
}) {
  const [questions, setQuestions] = useState<QuestionPublic[]>([]);
  // The WHOLE set's last position, as clinic-service names it — the finish line
  // the progress line is measured against (see progressCopy.ts). Undefined until
  // the first read, and on an older clinic-service that does not send it.
  const [lastQuestionIdx, setLastQuestionIdx] = useState<number | undefined>(undefined);
  const [answers, setAnswers] = useState<AnswerMap>(() =>
    seededAnswers && seededAnswers.length > 0 ? answersFromSeed(seededAnswers) : {},
  );
  const [currentKey, setCurrentKey] = useState<string | null>(null);
  // `restart` carries the HardStopRestartContext (or null for a capture-kind
  // stop — see `onRestart`'s doc above) so the "Back" button can hand it to
  // the parent unchanged.
  const [stopState, setStopState] = useState<{ result: EvaluationResult; restart: HardStopRestartContext | null } | null>(
    null,
  );
  // Chronological log of interactively-submitted (non-capture) answers —
  // replay material for a hard-stop "Back". Seeded from JourneyPage after a
  // resume remount so a second consecutive stop still has prior answers.
  // See `AnsweredEntry`'s doc above for why leading captures are excluded.
  const answerLogRef = useRef<AnsweredEntry[]>(seededAnswers ? [...seededAnswers] : []);
  const [softNote, setSoftNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [answerError, setAnswerError] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  // The rendered card's own "unsaved input ahead of Continue" — see DIRTY above.
  const [cardDirty, setCardDirty] = useState(false);
  const guard = useUnsavedChangesGuard(cardDirty);

  // Latest-value refs (NOT dependencies of `load`) so a parent re-render that
  // hands down new `capturePrefill`/`onCaptureChange` references never causes
  // the mount effect to refire `load()` — only `assessmentId` changing should.
  const capturePrefillRef = useRef(capturePrefill);
  capturePrefillRef.current = capturePrefill;
  const onCaptureChangeRef = useRef(onCaptureChange);
  onCaptureChangeRef.current = onCaptureChange;
  const initialQuestionKeyRef = useRef(initialQuestionKey);
  initialQuestionKeyRef.current = initialQuestionKey;
  const leadingCaptureSubmittedRef = useRef(leadingCaptureSubmitted);
  leadingCaptureSubmittedRef.current = leadingCaptureSubmitted;
  const seededAnswersRef = useRef(seededAnswers);
  seededAnswersRef.current = seededAnswers;

  // A fresh card never inherits the previous one's dirt.
  useEffect(() => {
    setCardDirty(false);
  }, [currentKey]);

  // Guards the whole load-and-auto-submit pipeline against firing twice
  // concurrently — e.g. React 18 StrictMode's dev-only double effect
  // invocation, which would otherwise double-submit the leading captures.
  const loadInFlightRef = useRef(false);
  // Set when this flow unmounts or the parent restarts — in-flight load/answer
  // work must not continue against a submitted or abandoned assessment.
  const cancelledRef = useRef(false);

  const refetch = useCallback(async (): Promise<QuestionPublic[]> => {
    const set = await getQuestions(assessmentId);
    setQuestions(set.questions);
    setLastQuestionIdx(set.lastQuestionIdx);
    return set.questions;
  }, [assessmentId]);

  const load = useCallback(() => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    cancelledRef.current = false;
    setLoadState('loading');

    (async () => {
      const qs = await refetch();
      if (cancelledRef.current) return;
      const sorted = [...qs].sort((a, b) => a.idx - b.idx);
      // RESUME: only trust the server's resume pointer if it is actually
      // present in THIS (freshly refetched) question set.
      const resumeKey = initialQuestionKeyRef.current;
      const resumeTarget = resumeKey && sorted.some((q) => q.key === resumeKey) ? resumeKey : null;
      const prefill = capturePrefillRef.current;
      const hasCapturePrefill = prefill.heightCm > 0 && prefill.weightKg > 0;
      const seedMap = seededAnswersRef.current?.length
        ? answersFromSeed(seededAnswersRef.current)
        : {};

      if (!hasCapturePrefill) {
        // GUARD (edge case): no usable Step-0 capture — never auto-submit
        // garbage. Fall back to rendering the leading question exactly as
        // before (it may be a `capture` question; that is fine, it just
        // renders normally and the patient fills it in) UNLESS a resume
        // pointer says otherwise.
        const first = allVisibleQuestions(sorted, seedMap)[0];
        if (!cancelledRef.current) setCurrentKey(resumeTarget ?? (first ? first.key : null));
        return;
      }

      onCaptureChangeRef.current(prefill.heightCm, prefill.weightKg);
      const capturedAnswers: AnswerMap = {};
      let sawSoftStop = false;

      if (leadingCaptureSubmittedRef.current) {
        // JourneyPage already posted leading captures during hard-stop
        // resume (capture-first). Mirror them into the local map only —
        // never re-POST.
        for (const question of sorted) {
          if (question.kind !== 'capture') break;
          capturedAnswers[question.key] = { heightCm: prefill.heightCm, weightKg: prefill.weightKg };
        }
      } else {
        // Every LEADING `capture`-kind question is auto-submitted, in idx
        // order, using the Step-0 values — never rendered as a screen.
        for (const question of sorted) {
          if (question.kind !== 'capture') break;
          if (cancelledRef.current) return;
          const result = await submitAnswer(assessmentId, clinicSlug, {
            questionKey: question.key,
            value: { heightCm: prefill.heightCm, weightKg: prefill.weightKg },
          });
          if (cancelledRef.current) return;
          capturedAnswers[question.key] = { heightCm: prefill.heightCm, weightKg: prefill.weightKg };
          if (result.outcome === 'hard_stop') {
            setAnswers((prev) => ({ ...prev, ...seedMap, ...capturedAnswers }));
            // A capture-kind stop (BMI floor) has no "different option" to
            // resume onto — see `onRestart`'s doc above.
            setStopState({ result, restart: null });
            return;
          }
          if (result.outcome === 'soft_stop') sawSoftStop = true;
        }
      }
      if (cancelledRef.current) return;
      setAnswers((prev) => ({ ...prev, ...seedMap, ...capturedAnswers }));
      if (sawSoftStop) setSoftNote(true);

      const mergedForVisibility = { ...seedMap, ...capturedAnswers };
      const first = nextVisibleAfter(sorted, mergedForVisibility, -1);
      setCurrentKey(resumeTarget ?? (first ? first.key : null));
    })()
      .then(() => {
        if (!cancelledRef.current) setLoadState('ready');
      })
      .catch(() => {
        if (!cancelledRef.current) setLoadState('error');
      })
      .finally(() => {
        loadInFlightRef.current = false;
      });
  }, [refetch, assessmentId, clinicSlug]);

  useEffect(() => {
    cancelledRef.current = false;
    load();
    return () => {
      cancelledRef.current = true;
    };
  }, [load]);

  const current = useMemo(() => questions.find((q) => q.key === currentKey), [questions, currentKey]);
  const visible = useMemo(() => visibleQuestions(questions, answers), [questions, answers]);

  // C2 — questionnaire_start: fire once when the first interactive question is
  // ready. WHY the resume gate: hard-stop "Back" mints a FRESH assessment and
  // remounts this flow with seededAnswers / leadingCaptureSubmitted /
  // initialQuestionKey. The patient is resuming mid-questionnaire, not starting
  // — a second start would corrupt the funnel. (Replay posts prior answers via
  // JourneyPage.submitAnswer, so handleAnswer never runs for them and C3 does
  // not burst; this gate only needs to suppress C2 on the remount.)
  useEffect(() => {
    if (loadState !== 'ready' || !leadId || !current) return;
    const isResume =
      (seededAnswers != null && seededAnswers.length > 0) ||
      leadingCaptureSubmitted ||
      Boolean(initialQuestionKey);
    if (isResume) return;
    if (questionnaireStartEmittedForAssessment.has(assessmentId)) return;
    questionnaireStartEmittedForAssessment.add(assessmentId);
    trackQuestionnaireStart({ leadId });
  }, [
    loadState,
    leadId,
    current,
    assessmentId,
    seededAnswers,
    leadingCaptureSubmitted,
    initialQuestionKey,
  ]);

  async function advance(answered: QuestionPublic, nextAnswers: AnswerMap, nextKey: string | null | undefined) {
    if (nextKey === null) {
      onComplete([...answerLogRef.current]);
      return;
    }
    let list = questions;
    if (typeof nextKey === 'string') {
      if (!list.some((q) => q.key === nextKey)) list = await refetch();
      if (list.some((q) => q.key === nextKey)) {
        setCurrentKey(nextKey);
        return;
      }
    }
    const afterIdx = list.find((q) => q.key === answered.key)?.idx ?? answered.idx;
    let next = nextVisibleAfter(list, nextAnswers, afterIdx);
    if (!next) {
      list = await refetch();
      next = nextVisibleAfter(list, nextAnswers, afterIdx);
    }
    if (next) setCurrentKey(next.key);
    else onComplete([...answerLogRef.current]);
  }

  async function handleAnswer(question: QuestionPublic, value: AnswerValue, detail?: string) {
    if (busy || cancelledRef.current) return;
    setBusy(true);
    setAnswerError(null);
    try {
      const result = await submitAnswer(assessmentId, clinicSlug, {
        questionKey: question.key,
        value,
        ...(detail ? { detail } : {}),
      });
      if (cancelledRef.current) return;
      const nextAnswers = { ...answers, [question.key]: value };
      setAnswers(nextAnswers);
      if (question.kind === 'capture' && typeof value === 'object' && !Array.isArray(value)) {
        onCaptureChange(value.heightCm, value.weightKg);
      }
      if (question.kind === 'date' && typeof value === 'string') {
        onDobChange?.(value);
      }
      // Snapshot replay material BEFORE logging this answer — a hard stop
      // must replay everything that came before it, never the answer that
      // triggered it (that is exactly the one the patient gets to redo).
      const priorAnswers = [...answerLogRef.current];
      if (question.kind !== 'capture') {
        answerLogRef.current.push({ questionKey: question.key, value, ...(detail ? { detail } : {}) });
      }
      // Clinical / UX state FIRST — then fire-and-forget telemetry. A GTM throw
      // must never land in this try's catch (that would paint "did not save" on
      // an answer that DID save and permanently suppress a hard_stop StopScreen).
      if (result.outcome === 'hard_stop') {
        setStopState({ result, restart: { stopQuestionKey: question.key, priorAnswers } });
      } else {
        if (result.outcome === 'soft_stop') setSoftNote(true);
        await advance(question, nextAnswers, result.nextQuestionKey);
      }
      // C3 — questionnaire_step: position only, never the answer. Reuse the
      // progress indicator's source of truth (`visible`) so step_number /
      // total_steps cannot drift from what the patient sees. After the state
      // transition so a thrown tag cannot swallow a hard stop. Capture-kind
      // auto-submits in load() never reach here; hard-stop replay posts via
      // JourneyPage.submitAnswer and likewise never reaches here.
      if (question.kind !== 'capture' && leadId) {
        const stepNumber = visible.findIndex((q) => q.key === question.key) + 1;
        const totalSteps = visible.length;
        if (stepNumber > 0) {
          trackQuestionnaireStep({ leadId, stepNumber, totalSteps });
        }
      }
    } catch {
      setAnswerError('Sorry — that did not save. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  function goBack() {
    if (!current) return;
    const before = visible.filter((q) => q.idx < current.idx);
    const previous = before[before.length - 1];
    if (previous) setCurrentKey(previous.key);
  }

  // THE BROWSER / PHONE BACK ON THE PATIENT JOURNEY (see "BACK IS BACK" below,
  // where the in-app Back is wired). Hooks, so above every early return.
  const backActionRef = useRef<(() => void) | undefined>(undefined);
  const browserBackBlocked = guard.blocker?.state === 'blocked';
  // One step back per blocked Back, however many renders the block spans.
  const backHandledRef = useRef(false);
  const [exitIntentOpen, setExitIntentOpen] = useState(false);
  // A Back pressed while ANOTHER dialog is up (the call back, opened from the
  // footer or "Why we ask") must close that dialog, not change the question
  // under it — on Android, Back is how a dialog is dismissed. The retention
  // dialog already knows how: mounted while the call-back dialog is open, it
  // draws nothing, cancels the navigation and closes the call back
  // (LeaveConfirmDialog, "BACK WHILE THE CALL-BACK DIALOG IS UP"). So that one
  // case still mounts it.
  const [backOverDialog, setBackOverDialog] = useState(false);
  useEffect(() => {
    if (!browserBackBlocked) {
      backHandledRef.current = false;
      setBackOverDialog(false);
      return;
    }
    if (staffView || backHandledRef.current) return;
    backHandledRef.current = true;
    if (exitIntentOpen) {
      // Back on our own exit-intent dialog dismisses it, and stays.
      setExitIntentOpen(false);
      guard.cancelLeave();
      return;
    }
    if (document.querySelector('[role="dialog"]')) {
      setBackOverDialog(true);
      return;
    }
    const back = backActionRef.current;
    if (back) {
      // Cancel the history pop (the guard's own entry stays), then step back in
      // the flow — the same thing the in-app Back does.
      guard.cancelLeave();
      back();
    } else {
      // Nowhere in the flow to go back to: let the browser's Back through.
      guard.confirmLeave();
    }
  }, [staffView, browserBackBlocked, guard, exitIntentOpen]);
  useExitIntent(!staffView && !stopState && loadState === 'ready', () => setExitIntentOpen(true));
  function stayFromRetention() {
    setExitIntentOpen(false);
    setBackOverDialog(false);
    guard.cancelLeave();
  }
  function leaveFromRetention() {
    setExitIntentOpen(false);
    setBackOverDialog(false);
    guard.confirmLeave();
  }

  if (stopState) {
    return (
      <StopScreen
        result={stopState.result}
        clinicSlug={clinicSlug}
        attribution={attribution}
        leadId={leadId}
        staffView={staffView}
        onRestart={() => onRestart(stopState.restart ?? undefined)}
        restartLabel="Back"
      />
    );
  }

  if (loadState === 'loading') {
    // A question-shaped skeleton (prompt line + a few option rows) rather than a
    // bare spinner, matching the card about to render (E3 #4).
    return (
      <JourneyScreen>
        <SkeletonCard lines={4} label="Loading your questions…" />
      </JourneyScreen>
    );
  }

  if (loadState === 'error' || !current) {
    return (
      <JourneyScreen>
        <ErrorState message="Sorry — we could not load your questions." onRetry={load} />
      </JourneyScreen>
    );
  }

  // The words above the question and the bar both come from progressCopy.ts,
  // measured in POSITIONS against the whole set's last idx. The served list
  // grows mid-journey as R28-withheld questions are released, so a count taken
  // from it used to move backwards. No "Question N of M" and no "N/M" step:
  // a total is the one thing the patient should never be shown a lot of.
  // The leading capture question (a 0/0 prefill: widget handoff, cold restore)
  // renders as a screen but is not a question the patient counts, so it keeps
  // its own label and the bar's floor.
  const position = visible.findIndex((q) => q.key === current.key);
  const servedIdx = questions.map((q) => q.idx);
  const answerableIdx = questions.filter((q) => q.kind !== 'capture').map((q) => q.idx);
  const servedLast = servedIdx.length > 0 ? Math.max(...servedIdx) : current.idx;
  const progressInput: ProgressInput = {
    currentIdx: current.idx,
    firstIdx: answerableIdx.length > 0 ? Math.min(...answerableIdx) : current.idx,
    // Never short of what is on screen: an older clinic-service omits the
    // field, and no finish line may sit behind a question already served.
    lastIdx: Math.max(lastQuestionIdx ?? servedLast, servedLast),
    knownAfter: visible.filter((q) => q.idx > current.idx).length,
  };
  const progress =
    current.kind === 'capture' ? { percent: 6, line: 'Confirm your details' } : questionProgress(progressInput);
  const positionLabel = progress.line;
  const percent = progress.percent;
  // THE LEAVE DIALOG'S "how much is left" (2026-09-25, CTO — the retention
  // moment, navigation/LeaveConfirmDialog.tsx). Off the SAME positions as the
  // line above, so the dialog can never say "nearly there" where the page does
  // not, nor print a count the line would not. The leading capture re-ask is not
  // a counted question, so it gets the generic words (null).
  const remaining: RemainingQuestions | null = current.kind === 'capture' ? null : remainingQuestions(progressInput);
  // BACK IS BACK — ON THE PATIENT JOURNEY IT NEVER ASKS (2026-09-26, Ashley
  // Haveloch-Jones via the CTO: "it puts me on this pop up rather than being
  // able to go back to the previous step uninterrupted"). The in-app Back goes
  // straight to the previous question (or off the first one), and a browser or
  // phone Back does the same (the effect below): whatever was typed on THIS
  // screen and not yet sent is dropped, which is what Back means. The answers
  // already sent are saved server-side and come back with their questions.
  // The retention dialog now opens only on EXIT INTENT (`useExitIntent`), and a
  // tab close or refresh still gets the browser's own "Leave site?" prompt from
  // the guard. The staff fill-on-behalf form keeps the classic ask-first Back.
  const backAction = position > 0 ? goBack : onExit;
  const guardedBack = backAction ? (staffView ? () => guard.guardedAction(backAction) : backAction) : undefined;
  // Read by the browser-Back effect, which sits above the early returns.
  backActionRef.current = backAction;

  // NO `step` PROP BELOW. The "N/M" chip went the same way as "Question N of M"
  // (2026-09-11): both counted the SERVED list, which grows as R28-withheld
  // questions are released, so both moved backwards — see progressCopy.ts. The
  // call-back offer is threaded as the screen's footer, and — through the
  // provider below, only when there is one — reachable from "Why we ask".
  // `gps-jux-question` is paint only (tokens.css P12, CTO 2026-09-25): the soft
  // bloom behind the heading and the halo round an enabled Continue. The
  // loading and error screens above do not wear it.
  const questionScreen = (
    <JourneyScreen onBack={guardedBack} percent={percent} footer={callbackOffer} className="gps-jux-question">
      {/* The patient journey's retention variant; the staff fill-on-behalf view
          (`staffView`) keeps the classic dialog — "you're nearly there" is
          written to the patient, not to the member of staff filling it in. */}
      {staffView ? (
        <LeaveConfirmDialog open={guard.promptOpen} onStay={guard.cancelLeave} onLeave={guard.confirmLeave} />
      ) : (
        <LeaveConfirmDialog
          journey={{ remaining, leaveLink: backOverDialog }}
          open={exitIntentOpen || (backOverDialog && guard.promptOpen)}
          onStay={stayFromRetention}
          onLeave={leaveFromRetention}
        />
      )}
      {softNote ? (
        <Alert tone="info" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
          Noted — a clinician will review this with you. You can carry on.
        </Alert>
      ) : null}
      {answerError ? (
        <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
          {answerError}
        </Alert>
      ) : null}
      {/* A light fade+rise on each question (keyed by question key) so answers
          feel like they turn over rather than snap-swap — reduced-motion → instant (E3 #3). */}
      <FadeIn resetKey={current.key}>
        <QuestionCard
          key={current.key}
          question={current}
          positionLabel={positionLabel}
          clinicSlug={clinicSlug}
          capturePrefill={capturePrefill}
          assessmentId={assessmentId}
          busy={busy}
          onSubmit={(value, detail) => void handleAnswer(current, value, detail)}
          onDirtyChange={setCardDirty}
          marketing={marketing}
        />
      </FadeIn>
    </JourneyScreen>
  );
  // `callbackOffer` is fixed for the life of a flow (JourneyPage always passes
  // one, the staff flow never does), so this never swaps the tree mid-journey.
  return callbackOffer ? <JourneyCallbackProvider>{questionScreen}</JourneyCallbackProvider> : questionScreen;
}
